<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '800'" :fullscreen="$vuetify.breakpoint.xsOnly" persistent scrollable>
    <v-card v-if="quotation">
      <v-toolbar dark color="primary" flat>
        <v-toolbar-title>{{ $t('quotations.accept.title') }} · {{ quotation.name }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :title="$t('close')" :disabled="busy" @click="dialog = false"><v-icon>close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text class="pa-0 gradient-icons">
        <v-stepper v-model="step" flat class="elevation-0">
          <v-stepper-header class="elevation-0">
            <v-stepper-step :step="1" :complete="step > 1" color="primary">{{ $t('quotations.accept.step_order') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :step="2" color="primary">{{ $t('quotations.accept.step_create') }}</v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content :step="1">
              <div class="body-2 mb-2">
                <v-icon class="mr-2">mdi-file-document-outline</v-icon>
                <span class="grey--text">{{ $t('quotations.accept.offer_code') }}:</span>
                {{ quotation.recipient.code }}
              </div>
              <div class="body-2 mb-4">
                <v-icon class="mr-2">mdi-trophy-outline</v-icon>
                <span class="grey--text">{{ $t('quotations.accept.orderer') }}:</span>
                {{ winnerText }}
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <date-picker :date="form.orderDate" :label="$t('quotations.accept.order_date')" :input-styles="{ outlined: true, dense: true }" @change="v => (form.orderDate = v)" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.orderReference" :label="$t('quotations.accept.order_reference')" :hint="$t('quotations.accept.order_reference_hint')" persistent-hint outlined dense>
                    <template v-slot:prepend><v-icon>mdi-receipt-text-outline</v-icon></template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content :step="2">
              <div class="caption grey--text text-uppercase mb-2">{{ $t('quotations.accept.project') }}</div>
              <v-text-field v-model="form.projectName" :label="$t('quotations.accept.project_name')" outlined dense hide-details>
                <template v-slot:prepend><v-icon>mdi-office-building</v-icon></template>
              </v-text-field>
              <div class="body-2 mt-2">
                <span class="grey--text">{{ $t('quotations.accept.project_code') }}:</span>
                {{ nextProjectCode || '–' }}
                <span class="caption grey--text">({{ $t('quotations.accept.project_code_next') }})</span>
                <span class="grey--text ml-3">{{ $t('quotations.accept.offer_code') }}:</span>
                {{ quotation.recipient.code }}
              </div>
              <div class="body-2 mt-1 mb-4">
                <span class="grey--text">{{ $t('quotations.accept.orderer') }}:</span>
                {{ winnerText }}
              </div>

              <!-- Kaikki työmaat perustetaan; muutokset tehdään projektille -->
              <div class="caption grey--text text-uppercase mb-1">{{ $t('quotations.accept.sites') }}</div>
              <v-simple-table dense class="mb-2">
                <tbody>
                  <tr v-for="s in sites" :key="s.id">
                    <td>{{ s.name }}</td>
                    <td class="text-no-wrap grey--text">{{ $tc('quotations.accept.works_count', s.works, { n: s.works }) }}</td>
                    <td class="text-right text-no-wrap">{{ $formatNumber(s.total, '0,0.00') }} €</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="d-flex justify-end align-baseline mt-4">
                <span class="caption grey--text mr-3">{{ $t('quotations.total_vat0') }}</span>
                <span class="title font-weight-bold">{{ $formatNumber(previewTotal, '0,0.00') }} €</span>
              </div>
              <v-alert v-if="elsewhereCount > 0" type="info" text dense class="mt-4 mb-0">
                {{ $tc('quotations.accept.siblings_elsewhere', elsewhereCount, { n: elsewhereCount }) }}
              </v-alert>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-divider />
      <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
        <v-btn v-if="step === 2" :block="$vuetify.breakpoint.xsOnly" text :disabled="busy" @click="step = 1">{{ $t('quotations.accept.back') }}</v-btn>
        <v-spacer />
        <v-btn :block="$vuetify.breakpoint.xsOnly" text :disabled="busy" @click="dialog = false">{{ $t('cancel') }}</v-btn>
        <v-btn v-if="step === 1" :block="$vuetify.breakpoint.xsOnly" :class="$vuetify.breakpoint.xsOnly && 'mb-2'" color="primary" @click="next">
          {{ $t('quotations.accept.next') }}
        </v-btn>
        <v-btn v-else :block="$vuetify.breakpoint.xsOnly" :class="$vuetify.breakpoint.xsOnly && 'mb-2'" color="primary" :loading="busy" @click="confirm">
          <v-icon left class="no-gradient">mdi-check</v-icon>
          {{ $t('quotations.accept.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs';
import quotationApi from '@/api/quotation';
import projectApi from '@/api/project';
import DatePicker from '@/components/DatePicker';
import { siteTotal, round2 } from '@/helpers/quotationCalc';

export default {
  name: 'AcceptQuotationDialog',
  components: { DatePicker },
  props: {
    parties: { type: Array, default: () => [] },
    persons: { type: Array, default: () => [] },
  },
  data() {
    return {
      dialog: false,
      step: 1,
      busy: false,
      quotation: null,
      nextProjectCode: null,
      form: { orderDate: null, orderReference: '', projectName: '' },
    };
  },
  computed: {
    sites() {
      return (this.quotation.sites || []).map(s => ({ id: s.id, name: s.name, works: (s.rows || []).filter(r => r.rowType === 'WORK').length, total: siteTotal(s) }));
    },
    winnerText() {
      return this.recipientText(this.quotation.recipient);
    },
    // Saman projektin muut tarjoukset, jotka merkitään toteutetuiksi toiselle tilaajalle (backend tekee saman rajauksen)
    elsewhereCount() {
      return (this.quotation.siblings || []).filter(s => ['REQUEST', 'CALCULATION', 'REVIEW', 'READY', 'NOT_ORDERED'].includes(s.status)).length;
    },
    previewTotal() {
      return round2(this.sites.reduce((sum, s) => sum + s.total, 0));
    },
  },
  methods: {
    recipientText(r) {
      const party = this.parties.find(p => p.id === r.partyId);
      const person = this.persons.find(p => p.id === r.personId);
      return [party ? party.companyName : '', person ? `${person.firstname} ${person.lastname}` : ''].filter(Boolean).join(' – ');
    },
    // Esikatselu: projekti saa numeron vasta perustettaessa, joten toinen samanaikainen perustaminen voi viedä sen
    async loadNextProjectCode() {
      this.nextProjectCode = null;
      try {
        this.nextProjectCode = await projectApi.getNextProjectCode();
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    open(quotation) {
      this.quotation = quotation;
      this.loadNextProjectCode();
      this.form = {
        orderDate: dayjs().format('YYYY-MM-DD'),
        orderReference: '',
        projectName: quotation.name,
      };
      this.step = 1;
      this.dialog = true;
    },
    next() {
      this.step = 2;
    },
    async confirm() {
      this.busy = true;
      try {
        const updated = await quotationApi.acceptQuotation(this.quotation.id, { ...this.form });
        this.$emit('accepted', updated);
        this.dialog = false;
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.busy = false;
    },
  },
};
</script>
